import type { LinkGrantFactorKind, ScanConfigResponse, SubmitScanRequest } from '@/api/types'
import type { TranslationKey } from '@/i18n/translations'

/**
 * Where a session's cursor is (build plan Track F, F2; docs/decisions.md D-57). Display-only: nothing
 * branches on it but the progress dot. Absent on every state reached outside a session - a
 * single-assessment link, which remains the overwhelming majority of traffic until Track G ships,
 * carries none of this and behaves exactly as it does today.
 */
export interface StepPosition {
  readonly index: number
  readonly total: number
  readonly isLast: boolean
}

/**
 * The whole patient journey as one discriminated union rather than several booleans, so an
 * impossible combination (mid-scan with no access token, say) cannot be represented and does not
 * need a test proving it cannot happen.
 */
export type FlowState =
  | { readonly step: 'challenging' }
  | { readonly step: 'date-of-birth'; readonly factor: LinkGrantFactorKind }
  | { readonly step: 'dead-link' }
  | { readonly step: 'rate-limited' }
  /**
   * The one state a reload can land on cold when the held credential is a SESSION token
   * (docs/decisions.md D-57): asks the server "given the session token I already hold, what should I
   * be looking at", which answers with the next `loading-config`, a `handing-off`, or
   * `session-complete`. A single-assessment link never enters this state - it goes straight from
   * `challenging` to `loading-config`, unchanged.
   */
  | { readonly step: 'resolving-step'; readonly sessionToken: string }
  | { readonly step: 'loading-config'; readonly accessToken: string; readonly position?: StepPosition }
  | { readonly step: 'scan-unavailable' }
  | {
      readonly step: 'ready'
      readonly accessToken: string
      readonly config: ScanConfigResponse
      readonly position?: StepPosition
    }
  /**
   * The tap has happened and the browser has not yet answered for the camera - which is exactly as
   * long as its permission dialog is up (scan-flow.tsx `onStart`).
   */
  | {
      readonly step: 'opening-camera'
      readonly accessToken: string
      readonly config: ScanConfigResponse
      readonly position?: StepPosition
    }
  | {
      readonly step: 'scanning'
      readonly accessToken: string
      readonly config: ScanConfigResponse
      readonly position?: StepPosition
    }
  | {
      readonly step: 'complete'
      /**
       * What was actually measured, for the patient to read - and only ever the fields the vendor
       * produced, because the submit shape omits what it did not (to-submit-request.ts).
       *
       * Absent on a hybrid scan whose reading was submitted thirty seconds in (docs/decisions.md
       * D-42), where this state is reached without the request in hand.
       */
      readonly results?: SubmitScanRequest
      readonly position?: StepPosition
    }
  /**
   * A scan that produced nothing, twice, after the patient had already been offered one retry.
   *
   * Its own step rather than `complete` with a flag, because the two screens make DIFFERENT promises:
   * one says a measurement was taken and sent, and this one must not - there is nothing to send. What
   * it does not do is mention the failure, which is the whole point of it.
   */
  | { readonly step: 'thanked'; readonly position?: StepPosition }
  /**
   * `scan-failed` NEVER auto-advances a session, even mid-session and even when a position is
   * present (docs/decisions.md D-57, `session-sequencer.ts`'s own doc comment). The backend leaves
   * the assessment live on a first failure, so there is nothing to advance TO yet - only `complete`
   * and `thanked` are the two backend-terminal outcomes `afterStepOutcome` acts on. `position` is
   * carried here only so a later state this screen leads to (a retry that succeeds, or a second
   * failure reaching `thanked`) still has it.
   */
  | {
      readonly step: 'scan-failed'
      // A key, not a sentence. The screen translates it, so nothing a patient reads is decided by
      // whichever component happened to construct this state - and an exception's own message can no
      // longer arrive here by being assigned to a string.
      readonly messageKey: TranslationKey
      readonly accessToken: string
      readonly config: ScanConfigResponse
      readonly position?: StepPosition
    }
  /**
   * Transient, between one step's own terminal outcome and the next `resolving-step`. No vendor SDK
   * is loaded here - it is a toast-shaped beat, per house rules, not a screen with prose
   * (docs/decisions.md D-57).
   */
  | { readonly step: 'advancing'; readonly sessionToken: string }
  /**
   * The session's current step is the external hand-off and its `redirectUrl` has not been stored
   * yet. The same neutral screen `advancing` shows, re-asking the server on an interval until the
   * redirect appears or `until` passes, at which point it becomes `dead-link`.
   *
   * A hold exists because the redirect is written by a peer that only learns the patient is here
   * once they OPEN the link, and that arrival leaves this service on an outbox poller rather than on
   * the request itself. `until` is carried IN the state, fixed on first entry, so that re-entering
   * this step on every poll cannot keep pushing the deadline away.
   */
  | {
      readonly step: 'awaiting-handoff'
      readonly sessionToken: string
      readonly position: StepPosition
      readonly until: number
    }
  /**
   * The session's current step is external. Nothing is measured here - `redirectUrl` is the URL
   * `scan-flow.tsx` sends the patient's browser to, as a same-origin auto-submitting form so the CSP
   * `form-action` directive is a real, browser-enforced check on it (build plan Track F, F3;
   * docs/decisions.md D-58). Already validated server-side, at write time, against this
   * installation's configured allow-list - `resolveAdvanceResponse` never constructs this state
   * without one, so a `Handoff` outcome missing it resolves to `'unusable'` instead.
   */
  | { readonly step: 'handing-off'; readonly position: StepPosition; readonly redirectUrl: string }
  /** Every step in the session reached a terminal outcome and none was an external hand-off. */
  | { readonly step: 'session-complete' }

/** Auth's own documented cap (architecture/services/platform-auth.md §5: "the max-attempts cap").
 * A public, architectural fact — not something read off any one response — see D-8. */
export const MAX_DATE_OF_BIRTH_ATTEMPTS = 3
